import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Plus, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
    getMeta,
    prescreenCase,
    createCase,
    getStoredEmail,
    setStoredEmail,
} from "@/lib/api";

const STEPS = ["TYPE", "DETAILS", "EVIDENCE", "PRESCREEN"];

const FormLabel = ({ children, required }) => (
    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 block mb-2">
        {children} {required && <span className="text-[#FF3B30]">*</span>}
    </label>
);

const TextInput = ({ value, onChange, placeholder, testId, type = "text" }) => (
    <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-[#111] border-2 border-white/20 px-4 py-3 font-sans text-white placeholder:text-white/30 focus:outline-none focus:border-white"
    />
);

const TextArea = ({ value, onChange, placeholder, testId, rows = 6 }) => (
    <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        data-testid={testId}
        className="w-full bg-[#111] border-2 border-white/20 px-4 py-3 font-sans text-white placeholder:text-white/30 focus:outline-none focus:border-white resize-none"
    />
);

const SelectBox = ({ value, onChange, options, placeholder, testId }) => (
    <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-[#111] border-2 border-white/20 px-4 py-3 font-sans text-white focus:outline-none focus:border-white appearance-none"
    >
        <option value="">{placeholder}</option>
        {options.map((o) =>
            typeof o === "string" ? (
                <option key={o} value={o}>
                    {o}
                </option>
            ) : (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            )
        )}
    </select>
);

const DRAFT_KEY = "hpm_case_draft";

export default function SubmitCase() {
    const nav = useNavigate();
    const [meta, setMeta] = useState(null);
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [prescreening, setPrescreening] = useState(false);
    const [prescreen, setPrescreen] = useState(null);

    const [form, setForm] = useState(() => {
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) return JSON.parse(raw);
        } catch (_) {}
        return {
            case_type: "",
            jurisdiction: "",
            state: "",
            language: "English",
            case_number: "",
            case_title: "",
            parties_plaintiff: "",
            parties_defendant: "",
            description: "",
            key_facts: "",
            evidence_submitted: [],
            user_email: getStoredEmail(),
            referral_code: "",
        };
    });
    const [evidenceInput, setEvidenceInput] = useState("");

    useEffect(() => {
        getMeta()
            .then(setMeta)
            .catch(() => setMeta({}));
    }, []);

    useEffect(() => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    }, [form]);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const canNext = () => {
        if (step === 0) return !!form.case_type && !!form.jurisdiction && !!form.language;
        if (step === 1)
            return (
                !!form.case_title &&
                !!form.parties_plaintiff &&
                !!form.parties_defendant &&
                (form.description || "").trim().length >= 50
            );
        if (step === 2) return true;
        return false;
    };

    const addEvidence = () => {
        const v = evidenceInput.trim();
        if (!v) return;
        update("evidence_submitted", [...(form.evidence_submitted || []), v]);
        setEvidenceInput("");
    };
    const removeEvidence = (i) =>
        update(
            "evidence_submitted",
            form.evidence_submitted.filter((_, idx) => idx !== i)
        );

    const runPrescreen = async () => {
        setPrescreening(true);
        setPrescreen(null);
        try {
            const r = await prescreenCase({
                case_type: form.case_type,
                jurisdiction: form.jurisdiction,
                description: form.description,
                key_facts: form.key_facts,
                evidence_submitted: form.evidence_submitted,
                parties_plaintiff: form.parties_plaintiff,
                parties_defendant: form.parties_defendant,
            });
            setPrescreen(r);
            if (r.viability === "pass") toast.success(`Prescreen passed — ${r.prescreen_score}/100`);
            else toast.error(`Prescreen ${r.prescreen_score}/100 — needs more detail`);
        } catch (e) {
            toast.error("Prescreen failed. Try again.");
        } finally {
            setPrescreening(false);
        }
    };

    const submitFinal = async () => {
        setSubmitting(true);
        try {
            if (form.user_email) setStoredEmail(form.user_email);
            const c = await createCase(form);
            toast.success("Case submitted. 10 bots running.");
            localStorage.removeItem(DRAFT_KEY);
            nav(`/hpm-analysis/${c.id}`);
        } catch (e) {
            toast.error("Submission failed. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const caseTypeOptions = meta?.case_types
        ? Object.entries(meta.case_types).flatMap(([_, items]) => items)
        : [];

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-12">
                {/* Progress */}
                <div className="border-2 border-white/20 mb-10" data-testid="wizard-progress">
                    <div className="grid grid-cols-4">
                        {STEPS.map((s, i) => (
                            <div
                                key={s}
                                className={`p-4 border-white/20 [&:not(:last-child)]:border-r-2 ${
                                    i === step
                                        ? "bg-white text-black"
                                        : i < step
                                          ? "bg-white/10 text-white"
                                          : "text-white/40"
                                }`}
                            >
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em]">
                                    STEP 0{i + 1}
                                </div>
                                <div className="font-anton text-xl uppercase mt-1">{s}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <h1 className="font-anton text-4xl sm:text-6xl uppercase leading-none mb-2">
                    {step === 0 && "Pick your fight."}
                    {step === 1 && "Tell the truth."}
                    {step === 2 && "Show your evidence."}
                    {step === 3 && "AI prescreen."}
                </h1>
                <p className="font-sans text-white/60 mb-10">
                    {step === 0 && "Select case type, jurisdiction, and language."}
                    {step === 1 && "Plain language. No legal jargon required."}
                    {step === 2 && "List everything — documents, witnesses, recordings."}
                    {step === 3 && "Run the 80% viability gate before the 10 bots engage."}
                </p>

                {/* STEP 0 */}
                {step === 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="step-type">
                        <div>
                            <FormLabel required>CASE TYPE</FormLabel>
                            <SelectBox
                                testId="select-case-type"
                                value={form.case_type}
                                onChange={(v) => update("case_type", v)}
                                options={caseTypeOptions}
                                placeholder="Select case type"
                            />
                        </div>
                        <div>
                            <FormLabel required>JURISDICTION</FormLabel>
                            <SelectBox
                                testId="select-jurisdiction"
                                value={form.jurisdiction}
                                onChange={(v) => update("jurisdiction", v)}
                                options={meta?.jurisdictions || []}
                                placeholder="Select jurisdiction"
                            />
                        </div>
                        <div>
                            <FormLabel>STATE (IF US)</FormLabel>
                            <TextInput
                                testId="input-state"
                                value={form.state}
                                onChange={(v) => update("state", v)}
                                placeholder="e.g. California"
                            />
                        </div>
                        <div>
                            <FormLabel required>LANGUAGE</FormLabel>
                            <SelectBox
                                testId="select-language"
                                value={form.language}
                                onChange={(v) => update("language", v)}
                                options={meta?.languages || []}
                                placeholder="Select language"
                            />
                        </div>
                        <div>
                            <FormLabel>CASE NUMBER / CITATION (OPTIONAL)</FormLabel>
                            <TextInput
                                testId="input-case-number"
                                value={form.case_number}
                                onChange={(v) => update("case_number", v)}
                                placeholder="e.g. 24-CV-1234"
                            />
                        </div>
                        <div>
                            <FormLabel>REFERRAL CODE (OPTIONAL)</FormLabel>
                            <TextInput
                                testId="input-referral"
                                value={form.referral_code}
                                onChange={(v) => update("referral_code", v)}
                                placeholder="REF-FIRMNAME-0000"
                            />
                        </div>
                    </div>
                )}

                {/* STEP 1 */}
                {step === 1 && (
                    <div className="grid grid-cols-1 gap-6" data-testid="step-details">
                        <div>
                            <FormLabel required>CASE TITLE</FormLabel>
                            <TextInput
                                testId="input-case-title"
                                value={form.case_title}
                                onChange={(v) => update("case_title", v)}
                                placeholder="Short title for your case"
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <FormLabel required>PLAINTIFF (YOU / WHO WAS WRONGED)</FormLabel>
                                <TextInput
                                    testId="input-plaintiff"
                                    value={form.parties_plaintiff}
                                    onChange={(v) => update("parties_plaintiff", v)}
                                    placeholder="Your name or party"
                                />
                            </div>
                            <div>
                                <FormLabel required>DEFENDANT (WHO DID IT)</FormLabel>
                                <TextInput
                                    testId="input-defendant"
                                    value={form.parties_defendant}
                                    onChange={(v) => update("parties_defendant", v)}
                                    placeholder="Other party"
                                />
                            </div>
                        </div>
                        <div>
                            <FormLabel required>DESCRIBE WHAT HAPPENED (50+ CHARS)</FormLabel>
                            <TextArea
                                testId="input-description"
                                value={form.description}
                                onChange={(v) => update("description", v)}
                                placeholder="In plain language: who, what, where, when, how."
                                rows={7}
                            />
                            <div className="font-mono text-[10px] text-white/40 mt-1">
                                {(form.description || "").length} / 50 minimum
                            </div>
                        </div>
                        <div>
                            <FormLabel>KEY FACTS / BULLET POINTS</FormLabel>
                            <TextArea
                                testId="input-key-facts"
                                value={form.key_facts}
                                onChange={(v) => update("key_facts", v)}
                                placeholder={"- Dates\n- Locations\n- Witnesses\n- Officer names / badge numbers"}
                                rows={5}
                            />
                        </div>
                        <div>
                            <FormLabel>YOUR EMAIL (OPTIONAL — TO TRACK CASE)</FormLabel>
                            <TextInput
                                testId="input-email"
                                value={form.user_email}
                                onChange={(v) => update("user_email", v)}
                                placeholder="you@example.com"
                                type="email"
                            />
                        </div>
                    </div>
                )}

                {/* STEP 2 */}
                {step === 2 && (
                    <div data-testid="step-evidence">
                        <div className="border-2 border-[#FF3B30] bg-[#FF3B30]/10 p-4 mb-6 flex gap-3">
                            <AlertTriangle size={20} className="text-[#FF3B30] flex-shrink-0" />
                            <div className="font-mono text-xs uppercase tracking-wider text-white/90">
                                Do NOT submit privileged or sensitive identifying info you would not share publicly.
                                HPM is information — not legal counsel.
                            </div>
                        </div>
                        <FormLabel>EVIDENCE ITEMS</FormLabel>
                        <div className="flex gap-2">
                            <TextInput
                                testId="input-evidence-item"
                                value={evidenceInput}
                                onChange={setEvidenceInput}
                                placeholder="e.g. Body cam footage requested 3/14"
                            />
                            <button
                                onClick={addEvidence}
                                className="btn-brutal"
                                data-testid="btn-add-evidence"
                            >
                                <Plus size={16} /> ADD
                            </button>
                        </div>

                        <div className="mt-6 space-y-2" data-testid="evidence-list">
                            {(form.evidence_submitted || []).length === 0 && (
                                <div className="font-mono text-xs text-white/40 uppercase tracking-wider">
                                    NO EVIDENCE LISTED YET
                                </div>
                            )}
                            {(form.evidence_submitted || []).map((e, i) => (
                                <div
                                    key={i}
                                    className="flex items-center justify-between border border-white/20 px-4 py-3 bg-[#111]"
                                    data-testid={`evidence-item-${i}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="font-mono text-[10px] text-white/40 uppercase">
                                            E{String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="font-sans text-sm">{e}</span>
                                    </div>
                                    <button
                                        onClick={() => removeEvidence(i)}
                                        className="text-white/60 hover:text-[#FF3B30]"
                                        data-testid={`btn-remove-evidence-${i}`}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* STEP 3 PRESCREEN */}
                {step === 3 && (
                    <div data-testid="step-prescreen">
                        {!prescreen && !prescreening && (
                            <div className="border-2 border-white/20 p-8 text-center">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">
                                    80% VIABILITY GATE
                                </div>
                                <h3 className="font-anton text-3xl uppercase mb-3">
                                    Ready to run prescreen?
                                </h3>
                                <p className="font-sans text-white/60 mb-6">
                                    Claude Sonnet 4.5 will rapidly evaluate viability before the full 10-bot analysis engages.
                                </p>
                                <button
                                    onClick={runPrescreen}
                                    className="btn-brutal btn-brutal-red"
                                    data-testid="btn-run-prescreen"
                                >
                                    RUN PRESCREEN <ArrowRight size={16} />
                                </button>
                            </div>
                        )}

                        {prescreening && (
                            <div className="border-2 border-white/20 p-12 text-center">
                                <Loader2 size={32} className="animate-spin mx-auto mb-4 text-[#FF3B30]" />
                                <div className="font-mono text-xs uppercase tracking-[0.25em] text-white/60">
                                    EVALUATING VIABILITY...
                                </div>
                            </div>
                        )}

                        {prescreen && (
                            <div
                                className={`border-2 p-8 ${
                                    prescreen.viability === "pass"
                                        ? "border-white/30"
                                        : "border-[#FF3B30]"
                                }`}
                                data-testid="prescreen-result"
                            >
                                <div className="flex items-baseline justify-between mb-6">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                                        PRESCREEN RESULT
                                    </div>
                                    <div
                                        className={`font-anton text-7xl ${
                                            prescreen.viability === "pass"
                                                ? "text-white"
                                                : "text-[#FF3B30]"
                                        }`}
                                        data-testid="prescreen-score"
                                    >
                                        {prescreen.prescreen_score}
                                    </div>
                                </div>

                                <div
                                    className={`inline-block font-mono text-xs uppercase tracking-[0.25em] px-3 py-1 mb-6 ${
                                        prescreen.viability === "pass"
                                            ? "bg-white text-black"
                                            : "bg-[#FF3B30] text-white"
                                    }`}
                                >
                                    {prescreen.viability === "pass" ? (
                                        <span className="flex items-center gap-2">
                                            <CheckCircle2 size={12} /> PASS — 80% GATE CLEARED
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            <AlertTriangle size={12} /> NEEDS WORK — BELOW 80%
                                        </span>
                                    )}
                                </div>

                                <p className="font-sans text-white/80 mb-6 leading-relaxed">
                                    {prescreen.summary}
                                </p>

                                {prescreen.improvement_tips?.length > 0 && (
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">
                                            IMPROVEMENT TIPS
                                        </div>
                                        <ul className="space-y-2">
                                            {prescreen.improvement_tips.map((t, i) => (
                                                <li
                                                    key={i}
                                                    className="flex gap-3 font-sans text-sm text-white/80"
                                                >
                                                    <span className="font-mono text-[#FF3B30]">→</span>
                                                    <span>{t}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="mt-8 flex flex-wrap gap-3">
                                    {prescreen.viability !== "pass" && (
                                        <button
                                            onClick={() => setStep(1)}
                                            className="btn-brutal"
                                            data-testid="btn-revise"
                                        >
                                            <ArrowLeft size={16} /> REVISE CASE
                                        </button>
                                    )}
                                    <button
                                        onClick={submitFinal}
                                        disabled={submitting}
                                        className="btn-brutal btn-brutal-red"
                                        data-testid="btn-submit-final"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" /> SUBMITTING...
                                            </>
                                        ) : (
                                            <>
                                                FIRE THE 10 BOTS <ArrowRight size={16} />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* NAV */}
                <div className="mt-12 flex justify-between items-center" data-testid="wizard-nav">
                    <button
                        onClick={() => setStep((s) => Math.max(0, s - 1))}
                        disabled={step === 0}
                        className="btn-brutal"
                        data-testid="btn-prev-step"
                    >
                        <ArrowLeft size={16} /> BACK
                    </button>
                    {step < 3 && (
                        <button
                            onClick={() => canNext() && setStep((s) => s + 1)}
                            disabled={!canNext()}
                            className="btn-brutal btn-brutal-red"
                            data-testid="btn-next-step"
                        >
                            NEXT STEP <ArrowRight size={16} />
                        </button>
                    )}
                </div>
            </div>

            <Footer disclaimer={meta?.disclaimer || ""} />
        </div>
    );
}
